<script setup>
import { ref, watch } from 'vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  title: { type: String, default: '' },
  description: { type: String, default: '' },
  confirmLabel: { type: String, default: '' },
  cancelLabel: { type: String, default: '' },
  loading: { type: Boolean, default: false },
  width: {
    type: String,
    default: 'lg',
    validator: value =>
      ['sm', 'md', 'lg', 'xl', '2xl', '3xl', '4xl', '5xl', 'full'].includes(
        value
      ),
  },
  hideFooter: { type: Boolean, default: false },
  closeOnOutside: { type: Boolean, default: true },
});

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel']);

const dialogRef = ref(null);

const closeDialog = () => {
  if (props.modelValue) {
    emit('update:modelValue', false);
  }
  emit('cancel');
};

const openDialog = () => {
  dialogRef.value?.open();
};

watch(
  () => props.modelValue,
  value => {
    if (value) {
      openDialog();
    } else {
      dialogRef.value?.close();
    }
  },
  { immediate: true }
);

const handleConfirm = () => {
  emit('confirm');
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="title"
    :description="description"
    :width="width"
    :show-cancel-button="!hideFooter && (cancelLabel || confirmLabel)"
    :show-confirm-button="!hideFooter && !!confirmLabel"
    :cancel-button-label="cancelLabel || undefined"
    :confirm-button-label="confirmLabel || undefined"
    :is-loading="loading"
    :close-on-outside="closeOnOutside"
    @close="closeDialog"
    @confirm="handleConfirm"
  >
    <div class="flex flex-col gap-4">
      <slot />
    </div>
    <template v-if="!hideFooter" #footer>
      <slot name="footer" />
    </template>
  </Dialog>
</template>
